import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Tabs,
  Tab,
  LinearProgress,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { VendorHeader } from './VendorHeader';
import axios from 'axios';
import { KeyUri } from '../key';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import { State, City } from 'country-state-city';

import { BasicInformationSection } from './BasicInformationSection';
import { LocationSection } from './LocationSection';
import { ContactSection } from './ContactSection';
import { AmenitiesSection } from './AmenitiesSection';
import AccommodationsSection from './AccomodationSection';
import { PoliciesSection } from './policiesSection';

const vendorToken = localStorage.getItem('vendorToken') || null;

export function PropertyEditPage({ propertyId, onBack }) {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const TAB_ORDER = ['basic', 'location', 'contact', 'accommodations', 'amenities', 'policies'];

  useEffect(() => {
    async function fetchProperty() {
      if (!vendorToken) {
        toast.error('Authentication required. Please login.');
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${KeyUri.BACKEND_URI}/get-property/${propertyId}`, {
          headers: { Authorization: `Bearer ${vendorToken}` },
        });
        const raw = response.data.data;

        // If the vendor already has changes waiting for approval, show those
        // (the live listing stays untouched on the server).
        const propertyData = {
          ...raw,
          ...(raw.pendingChanges || {}),
          status: raw.status,
          changeRequestStatus: raw.changeRequestStatus || 'none',
          rejectionReason: raw.rejectionReason || '',
        };

        const normalizedAccommodations = (propertyData.accommodations || []).map((acc) => ({
          ...acc,
          mealPlanPricing: {
            CP: { B2B: acc.mealPlanPricing?.CP?.B2B || '', B2C: acc.mealPlanPricing?.CP?.B2C || '' },
            MAP: { B2B: acc.mealPlanPricing?.MAP?.B2B || '', B2C: acc.mealPlanPricing?.MAP?.B2C || '' },
            AP: { B2B: acc.mealPlanPricing?.AP?.B2B || '', B2C: acc.mealPlanPricing?.AP?.B2C || '' },
          },
        }));

        const STATES = State.getStatesOfCountry('IN');

        let stateIsoCode = '';
        let cityName = propertyData.location?.city || '';

        if (propertyData.location?.state) {
          const matchedState = STATES.find(
            (s) => s.name.toLowerCase() === propertyData.location.state.toLowerCase()
          );
          stateIsoCode = matchedState ? matchedState.isoCode : '';

          if (matchedState && cityName) {
            const cities = City.getCitiesOfState('IN', matchedState.isoCode);
            const matchedCity = cities.find((c) =>
              c.name.toLowerCase().includes(cityName.toLowerCase())
            );
            if (matchedCity) cityName = matchedCity.name;
          }
        }

        setFormData({
          ...propertyData,
          accommodations: normalizedAccommodations,

          state: stateIsoCode,
          city: cityName,

          address: propertyData.location?.address || '',
          pincode: propertyData.location?.pincode || '',
          latitude: propertyData.location?.latitude || '',
          longitude: propertyData.location?.longitude || '',
          mapShareLink: propertyData.location?.mapShareLink || '',
          how_to_reach: propertyData.location?.how_to_reach || {
            overview: '',
            by_air: { airport_name: '', airport_code: '', distance_km: '', distance_label: '', instructions: '' },
            by_train: { station_name: '', station_code: '', distance_km: '', distance_label: '', instructions: '' },
            by_road: { distance_label: '', instructions: '', bus_stand: '' },
            local_transfer: { available: false, details: '' },
          },
          contactPerson: propertyData.contact?.person || '',
          contactEmail: propertyData.contact?.email || '',
          contactPhone: propertyData.contact?.phone || '',

          location: propertyData.location || {},
          contact: propertyData.contact || {},
          amenities: propertyData.amenities || { general: [], recreational: [], dining: [], services: [] },
          policies: propertyData.policies || {},
          languages: propertyData.languages || [],
          accessibility: propertyData.accessibility || [],
        });

        setErrors({});
        setActiveTab('basic');
      } catch (error) {
        toast.error('Failed to load property data.');
        console.error('Fetch error:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchProperty();
  }, [propertyId]);

  const validateField = (field, value) => {
    const errors = {};

    switch (field) {
      case 'propertyName':
        if (!value?.trim()) errors.propertyName = 'Property name is required';
        else if (value.trim().length < 3) errors.propertyName = 'Property name must be at least 3 characters';
        else if (value.trim().length > 100) errors.propertyName = 'Property name cannot exceed 100 characters';
        break;
      case 'description':
        if (!value?.trim()) errors.description = 'Description is required';
        else if (value.trim().length < 50) errors.description = 'Description must be at least 50 characters';
        else if (value.trim().length > 2000) errors.description = 'Description cannot exceed 2000 characters';
        break;
      case 'propertyType':
        if (!value) errors.propertyType = 'Property type is required';
        break;
      case 'checkInTime':
      case 'checkOutTime':
        if (!value) errors[field] = `${field.replace('Time', ' time')} is required`;
        else if (!/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value))
          errors[field] = 'Please provide valid time format (HH:MM)';
        break;
      case 'pincode':
        if (!value?.trim()) errors.pincode = 'Pincode is required';
        else if (!/^\d{6}$/.test(value)) errors.pincode = 'Please provide a valid 6-digit pincode';
        break;
      case 'contactPerson':
        if (!value?.trim()) errors.contactPerson = 'Contact person name is required';
        else if (value.trim().length < 3) errors.contactPerson = 'Name must be at least 3 characters';
        break;
      case 'contactEmail':
        if (!value?.trim()) errors.contactEmail = 'Email is required';
        else if (!/^\S+@\S+\.\S+$/.test(value)) errors.contactEmail = 'Please provide a valid email';
        break;
      case 'contactPhone':
        if (!value?.trim()) errors.contactPhone = 'Phone number is required';
        else {
          const cleanPhone = value.replace(/[\s\-\(\)\+]/g, '');
          if (cleanPhone.length !== 10) {
            errors.contactPhone = `Phone number must be exactly 10 digits (you entered ${cleanPhone.length})`;
          }
        }
        break;
      default:
        break;
    }
    return errors;
  };

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const keys = field.split('.');
      setFormData((prev) => {
        let updated = { ...prev };
        let obj = updated;
        for (let i = 0; i < keys.length - 1; i++) {
          obj[keys[i]] = { ...(obj[keys[i]] || {}) };
          obj = obj[keys[i]];
        }
        obj[keys[keys.length - 1]] = value;
        return updated;
      });
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }

    const fieldErrors = validateField(field, value);
    if (Object.keys(fieldErrors).length === 0 && errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    } else if (Object.keys(fieldErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...fieldErrors }));
    }
  };

  const validateForm = (scope = 'all') => {
    const newErrors = {};
    const fieldsToValidate =
      scope === 'all'
        ? ['propertyName', 'propertyType', 'description', 'checkInTime', 'checkOutTime',
           'address', 'city', 'state', 'pincode', 'contactPerson', 'contactEmail', 'contactPhone']
        : scope === 'basic'
        ? ['propertyName', 'propertyType', 'description', 'checkInTime', 'checkOutTime']
        : scope === 'location'
        ? ['address', 'city', 'state', 'pincode']
        : scope === 'contact'
        ? ['contactPerson', 'contactEmail', 'contactPhone']
        : [];

    fieldsToValidate.forEach((field) => {
      const fieldErrors = validateField(field, formData?.[field]);
      Object.assign(newErrors, fieldErrors);
    });

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  // Jump to the first tab that has an error so the vendor can see it
  const goToFirstErrorTab = (errs) => {
    const keys = Object.keys(errs);
    if (keys.some((k) => ['propertyName', 'propertyType', 'description', 'checkInTime', 'checkOutTime'].includes(k)))
      setActiveTab('basic');
    else if (keys.some((k) => ['address', 'city', 'state', 'pincode'].includes(k))) setActiveTab('location');
    else if (keys.some((k) => ['contactPerson', 'contactEmail', 'contactPhone'].includes(k))) setActiveTab('contact');
  };

  const nextTab = () => {
    const isValid = validateForm(activeTab);
    if (!isValid) {
      toast.error('Please fix errors on this step before continuing.');
      return;
    }
    const idx = TAB_ORDER.indexOf(activeTab);
    if (idx < TAB_ORDER.length - 1) setActiveTab(TAB_ORDER[idx + 1]);
  };

  const prevTab = () => {
    const idx = TAB_ORDER.indexOf(activeTab);
    if (idx > 0) setActiveTab(TAB_ORDER[idx - 1]);
  };

  // action: 'draft' | 'submit' | 'update'
  const saveProperty = async (action) => {
    if (!vendorToken) {
      toast.error('Authentication required.');
      return;
    }

    // Drafts may be incomplete; everything else must pass validation
    if (action !== 'draft') {
      if (!validateForm('all')) {
        toast.error('Please fix all errors before submitting.');
        // validateForm sets state asynchronously, so re-run quickly to find the tab
        const quickErrors = {};
        ['propertyName', 'propertyType', 'description', 'checkInTime', 'checkOutTime',
         'address', 'city', 'state', 'pincode', 'contactPerson', 'contactEmail', 'contactPhone']
          .forEach((f) => Object.assign(quickErrors, validateField(f, formData?.[f])));
        goToFirstErrorTab(quickErrors);
        return;
      }
      if (!formData.accommodations?.length) {
        toast.error('Please add at least one accommodation type');
        setActiveTab('accommodations');
        return;
      }
    }

    setSaving(true);
    try {
      const STATES = State.getStatesOfCountry('IN');
      const stateIsoCode = formData.state;
      const stateName = stateIsoCode
        ? STATES.find((s) => s.isoCode === stateIsoCode)?.name || stateIsoCode
        : '';

      const cleanedAccommodations = (formData.accommodations || []).map((acc) => {
        const mealPlanPricing = {};
        ['CP', 'MAP', 'AP'].forEach((plan) => {
          const b2b = Number(acc.mealPlanPricing?.[plan]?.B2B);
          const b2c = Number(acc.mealPlanPricing?.[plan]?.B2C);
          mealPlanPricing[plan] = {
            ...(b2b >= 500 ? { B2B: b2b } : {}),
            ...(b2c >= 500 ? { B2C: b2c } : {}),
          };
        });
        return { ...acc, mealPlanPricing };
      });

      // Never send status / approval fields: the server decides them from `action`
      const {
        status,
        pendingChanges,
        changeRequestStatus,
        rejectionReason,
        submittedAt,
        changesSubmittedAt,
        ...rest
      } = formData;

      const payload = {
        ...rest,
        action,
        accommodations: cleanedAccommodations,
        state: stateName,
        city: formData.city,
        location: {
          address: formData.address,
          city: formData.city,
          state: stateName,
          pincode: formData.pincode,
          latitude: formData.latitude,
          longitude: formData.longitude,
          mapShareLink: formData.mapShareLink,
          how_to_reach: formData.how_to_reach || {},
        },
        contact: {
          person: formData.contactPerson,
          email: formData.contactEmail,
          phone: formData.contactPhone,
        },
      };

      const res = await axios.put(`${KeyUri.BACKEND_URI}/update-property/${propertyId}`, payload, {
        headers: { Authorization: `Bearer ${vendorToken}` },
      });

      const saved = res.data.data;
      setFormData((prev) => ({
        ...prev,
        status: saved.status,
        changeRequestStatus: saved.changeRequestStatus,
        rejectionReason: '',
      }));
      toast.success(res.data.message || 'Saved!');

      if (action !== 'draft' && onBack) onBack();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save property.');
      console.error('Update error:', error);
    } finally {
      setSaving(false);
    }
  };

  const getProgress = () => {
    if (!formData) return 0;
    let completed = 0;
    const total = 6;

    if (formData.propertyName && formData.propertyType && formData.description) completed++;
    if (formData.address && formData.city && formData.state && formData.pincode) completed++;
    if (formData.contactPerson && formData.contactEmail && formData.contactPhone) completed++;
    if (formData.accommodations?.length > 0) completed++;
    const hasAmenities = Object.values(formData.amenities || {}).some((arr) => arr.length > 0);
    if (hasAmenities) completed++;
    if (formData.policies?.cancellation || formData.languages?.length > 0) completed++;

    return (completed / total) * 100;
  };

  if (loading) return <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 8 }} />;
  if (!formData) return <Typography color="error">Property not found</Typography>;

  const isDraftLike = formData.status === 'draft' || formData.status === 'rejected';
  const isLive = formData.status === 'approved';

  return (
    <Box>
      <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200 }}>
        <VendorHeader />
      </Box>

      <Box sx={{ maxWidth: 900, mx: 'auto', px: 2, mb: 8, mt: 2 }}>
        <Box sx={{ mb: 2, mr: 24 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            size="small"
            sx={{
              p: 1.5, mr: 4, borderRadius: 1, textTransform: 'none',
              fontWeight: 500, boxShadow: 1, minWidth: 'auto',
              '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' },
              transition: 'all 0.2s ease',
            }}
          >
            Back
          </Button>
        </Box>

        <Card sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%' }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                Edit Property: {formData.propertyName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(getProgress())}% Complete
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={getProgress()} sx={{ height: 8, borderRadius: 1 }} />
          </Box>

          {/* Status notices */}
          {formData.status === 'pending' && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              This property is awaiting admin approval.
            </Alert>
          )}
          {formData.status === 'rejected' && formData.rejectionReason && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Rejected: {formData.rejectionReason}. Fix the issues and submit again.
            </Alert>
          )}
          {isLive && formData.changeRequestStatus === 'pending' && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Your changes are awaiting admin approval. The live listing is unchanged until then.
            </Alert>
          )}
          {isLive && formData.changeRequestStatus === 'rejected' && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Your last changes were rejected{formData.rejectionReason ? `: ${formData.rejectionReason}` : '.'} You can edit and resubmit.
            </Alert>
          )}
          {isLive && formData.changeRequestStatus === 'none' && (
            <Alert severity="info" sx={{ mb: 3 }}>
              This property is live. Any changes you make will be reviewed by an admin before they go live.
            </Alert>
          )}

          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? false : 'auto'}
            sx={{ mb: 4 }}
          >
            <Tab label="Basic Info" value="basic" />
            <Tab label="Location" value="location" />
            <Tab label="Contact" value="contact" />
            <Tab label="Rooms" value="accommodations" />
            <Tab label="Amenities" value="amenities" />
            <Tab label="Policies" value="policies" />
          </Tabs>

          {activeTab === 'basic' && (
            <BasicInformationSection formData={formData} onChange={handleChange} errors={errors} />
          )}
          {activeTab === 'location' && (
            <LocationSection formData={formData} onChange={handleChange} errors={errors} />
          )}
          {activeTab === 'contact' && (
            <ContactSection formData={formData} onChange={handleChange} errors={errors} />
          )}
          {activeTab === 'accommodations' && (
            <AccommodationsSection formData={formData} onChange={handleChange} errors={errors} />
          )}
          {activeTab === 'amenities' && (
            <AmenitiesSection formData={formData} onChange={handleChange} errors={errors} />
          )}
          {activeTab === 'policies' && (
            <PoliciesSection formData={formData} onChange={handleChange} errors={errors} />
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4, pt: 3, borderTop: '1px solid #ddd' }}>
            {activeTab !== 'basic' && (
              <Button variant="outlined" onClick={prevTab}>
                Back
              </Button>
            )}
            {activeTab !== 'policies' && (
              <Button variant="contained" onClick={nextTab}>
                Next
              </Button>
            )}

            {isDraftLike ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={() => saveProperty('draft')}
                  disabled={saving}
                >
                  Save Draft
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => saveProperty('submit')}
                  disabled={saving}
                  sx={{ flexGrow: 1 }}
                >
                  {saving ? 'Submitting...' : 'Submit for Approval'}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() => saveProperty('update')}
                disabled={saving}
                sx={{ flexGrow: 1 }}
              >
                {saving
                  ? 'Saving...'
                  : isLive
                  ? 'Submit Changes for Approval'
                  : 'Update Property'}
              </Button>
            )}
          </Stack>
        </Card>
      </Box>
    </Box>
  );
}